import {
  PlusOutlined,
  UserAddOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Tabs,
  Tooltip,
} from "antd";
import { AdminFileManager, buildMediaFullUrl, StickyFormActions } from "@components";
import {
  Api,
  canManageContentActivation,
  ICharacter,
  ICharacterMetadata,
  TtsVoice,
} from "@model";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  type CSSProperties,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useIsCompact } from "../../hooks/useIsCompact";
import { CharacterMessagesPanel } from "./CharacterMessagePage";
import {
  CHARACTER_LANGUAGES,
  cleanCharacterMetadata,
  migrateCharacterMetadataFromLegacy,
  normalizeCharacterMetadata,
  talkSubjectsFromFormValue,
} from "./characterLanguages";
import {
  CHARACTER_INSTRUCTION_PLACEHOLDERS_HINT_STYLE,
  CHARACTER_INSTRUCTION_PLACEHOLDERS_TEXT,
} from "./characterInstructionPlaceholders";

const METADATA_SECTION_STYLE: CSSProperties = {
  background: "#fafafa",
  border: "1px solid #f0f0f0",
  borderRadius: 8,
  padding: "12px 16px 4px",
  marginBottom: 16,
};

const METADATA_SECTION_LABEL_STYLE: CSSProperties = {
  marginBottom: 10,
  fontSize: 12,
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.55)",
  letterSpacing: 0.3,
};

const toStoredMediaPath = (fullUrl: string): string => {
  try {
    return new URL(fullUrl).pathname.replace(/^\//, "");
  } catch {
    return fullUrl.replace(/^https?:\/\/[^/]+\//, "").replace(/^\//, "");
  }
};

const mediaPreviewSrc = (path?: string) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return buildMediaFullUrl(path);
};

const uploadCharacterAvatarFromPath = async (
  characterId: string,
  sourcePath: string,
) => {
  const response = await fetch(mediaPreviewSrc(sourcePath));
  if (!response.ok) {
    throw new Error("Failed to fetch image");
  }
  const blob = await response.blob();
  const ext = sourcePath.match(/\.(\w+)$/)?.[1] ?? "jpg";
  const file = new File([blob], `avatar.${ext}`, {
    type: blob.type || "image/jpeg",
  });
  const formData = new FormData();
  formData.append("file", file);
  await Api.post(
    `/api/admin/panel/characters/files/${characterId}.jpg`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

type CharacterMetadataTranslation = Record<
  string,
  {
    name?: string;
    description?: string;
    starterText?: string;
    talkSubjects?: string[] | string;
  }
>;

function isNaturalOrChirpVoice(name: string): boolean {
  const normalized = name.toLowerCase();
  return (
    normalized.includes("chirp") ||
    normalized.includes("neural") ||
    normalized.includes("natural")
  );
}

function randomNaturalOrChirpVoice(voices: TtsVoice[]): string | undefined {
  const matching = voices.filter((voice) =>
    isNaturalOrChirpVoice(voice.name ?? ""),
  );
  if (matching.length === 0) return undefined;
  return matching[Math.floor(Math.random() * matching.length)].name;
}

function mergeCharacterMetadataTranslations(
  current: ICharacterMetadata | undefined,
  translations: CharacterMetadataTranslation,
): ICharacterMetadata {
  const next = { ...(current ?? {}) };
  for (const [langKey, fields] of Object.entries(translations)) {
    if (langKey === "en" || !fields) continue;
    const talkSubjects = talkSubjectsFromFormValue(fields.talkSubjects);
    next[langKey] = {
      ...next[langKey],
      ...(fields.name?.trim() ? { name: fields.name.trim() } : {}),
      ...(fields.description?.trim()
        ? { description: fields.description.trim() }
        : {}),
      ...(fields.starterText?.trim()
        ? { starterText: fields.starterText.trim() }
        : {}),
      ...(talkSubjects.length ? { talkSubjects: talkSubjects.join("\n") } : {}),
    };
  }
  return next;
}

function renderMetadataTranslateButton(options: {
  loading: boolean;
  onClick: () => void;
  tooltip: string;
}) {
  return (
    <Tooltip title={options.tooltip}>
      <Button
        type="text"
        aria-label={options.tooltip}
        icon={<RobotOutlined />}
        loading={options.loading}
        onClick={options.onClick}
      />
    </Tooltip>
  );
}

export default function CharacterCreatePage() {
  const { state: locationState } = useLocation();
  const returnTo =
    locationState &&
    typeof locationState === "object" &&
    "returnTo" in locationState
      ? (locationState as { returnTo?: string }).returnTo
      : undefined;
  const state =
    locationState &&
    typeof locationState === "object" &&
    "returnTo" in locationState
      ? (() => {
          const { returnTo: _, ...rest } = locationState as {
            returnTo?: string;
            [k: string]: unknown;
          };
          return rest as unknown as ICharacter | undefined;
        })()
      : (locationState as ICharacter | undefined);
  const { id } = useParams<{ id: string }>();
  const isMounted = useRef(true);
  const initKeyRef = useRef<string | null>(null);
  const [form] = Form.useForm();
  const [selectedAvatarPath, setSelectedAvatarPath] = useState<string>();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentChar, setCurrentChar] = useState<ICharacter | undefined>(state);
  const [voicesByLang, setVoicesByLang] = useState<Record<string, TtsVoice[]>>(
    {},
  );
  const [loadingVoicesByLang, setLoadingVoicesByLang] = useState<
    Record<string, boolean>
  >({});
  const [playingVoiceLang, setPlayingVoiceLang] = useState<string>();
  const [translatingCharacterMetadata, setTranslatingCharacterMetadata] =
    useState(false);
  const [activeLanguageTab, setActiveLanguageTab] = useState<string>(
    CHARACTER_LANGUAGES[0].key,
  );
  const isCreateMode = !id && !state?._id;
  const isCompact = useIsCompact();
  const canManageActivation = canManageContentActivation();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const characterIdForMessages = useMemo(
    () => id ?? currentChar?._id ?? state?._id ?? "",
    [id, currentChar?._id, state?._id],
  );

  const activeCharacterTab =
    !isCreateMode && searchParams.get("tab") === "messages"
      ? "messages"
      : "edit";

  const setCharacterTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "messages") {
      next.set("tab", "messages");
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };

  const applyCharacterToForm = useCallback(
    (characterData: ICharacter) => {
      const metadata = migrateCharacterMetadataFromLegacy(characterData);
      setCurrentChar({ ...characterData, metadata });
      form.setFieldsValue({
        ...characterData,
        metadata,
      });
      setSelectedAvatarPath(undefined);
      const configuredKeys = Object.keys(cleanCharacterMetadata(metadata));
      if (configuredKeys.length > 0) {
        setActiveLanguageTab(configuredKeys[0]);
      }
    },
    [form],
  );

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchCharacterById = useCallback(
    async (characterId: string) => {
      try {
        const response = await Api.get(
          `/api/admin/panel/characters/${characterId}`,
        );
        const characterData = response.data?.data as ICharacter | undefined;
        if (!isMounted.current) return;
        if (!characterData?._id) {
          message.error("Character not found");
          return;
        }
        applyCharacterToForm(characterData);
      } catch {
        if (isMounted.current) message.error("Failed to load character");
      }
    },
    [applyCharacterToForm],
  );

  useEffect(() => {
    if (id) {
      if (initKeyRef.current === id) return;
      initKeyRef.current = id;
      void fetchCharacterById(id);
      return;
    }
    if (state) {
      const key = state._id ?? "create";
      if (initKeyRef.current === key) return;
      initKeyRef.current = key;
      applyCharacterToForm(state);
    }
  }, [state, id, applyCharacterToForm, fetchCharacterById]);

  const fetchVoicesForLanguage = useCallback(
    async (voiceCode: string): Promise<TtsVoice[]> => {
      if (!voiceCode) return [];
      setLoadingVoicesByLang((prev) => ({ ...prev, [voiceCode]: true }));
      try {
        const response = await Api.get(
          `/api/tts/voices?languageCode=${voiceCode}`,
        );
        const voices = (response.data?.data ?? []) as TtsVoice[];
        if (isMounted.current) {
          setVoicesByLang((prev) => ({
            ...prev,
            [voiceCode]: voices,
          }));
        }
        return voices;
      } catch {
        if (isMounted.current) {
          message.error(`Failed to fetch voices for ${voiceCode}`);
          setVoicesByLang((prev) => ({ ...prev, [voiceCode]: [] }));
        }
        return [];
      } finally {
        if (isMounted.current) {
          setLoadingVoicesByLang((prev) => ({ ...prev, [voiceCode]: false }));
        }
      }
    },
    [],
  );

  useEffect(() => {
    CHARACTER_LANGUAGES.forEach((lang) => {
      fetchVoicesForLanguage(lang.voiceCode);
    });
  }, [fetchVoicesForLanguage]);

  const createCharacter = async (values: ICharacter) => {
    const metadata = cleanCharacterMetadata(values.metadata);
    const enMeta = metadata.en;
    const payload: ICharacter = {
      ...values,
      metadata,
      description: enMeta?.description ?? values.description ?? "",
      starterText: enMeta?.starterText ?? values.starterText,
      talkSubjects: talkSubjectsFromFormValue(enMeta?.talkSubjects),
    };
    if (!canManageActivation) delete (payload as Partial<ICharacter>).isActive;

    if (!payload.description?.trim()) {
      message.error("Add a bio in the English metadata tab");
      return;
    }

    setSaving(true);
    try {
      const response = isCreateMode
        ? await Api.post("/api/admin/panel/characters", payload)
        : await Api.put(
            `/api/admin/panel/characters/${payload._id ?? ""}`,
            payload,
          );

      const saved = response.data?.data;
      const ok = isCreateMode ? Boolean(saved?._id) : response.status === 200;

      if (!isMounted.current) return;

      if (!ok) {
        message.error(
          isCreateMode
            ? "An error occurred while creating character"
            : "An error occurred",
        );
        return;
      }

      const characterId = saved?._id ?? payload._id;
      if (selectedAvatarPath && characterId) {
        try {
          await uploadCharacterAvatarFromPath(
            characterId,
            selectedAvatarPath,
          );
        } catch {
          message.error("Failed to upload the image.");
        }
      }

      message.success(
        isCreateMode ? "Character created successfully" : "Saved",
      );
      navigate(returnTo ?? "/panel/characters");
    } catch {
      if (isMounted.current) message.error("An error occurred");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const playTestVoice = async (langKey: string, voiceCode: string) => {
    const localizedName = form.getFieldValue(["metadata", langKey, "name"]);
    const voice = form.getFieldValue(["metadata", langKey, "voice"]);
    const fallbackName = form.getFieldValue("name") || "Character";

    if (!voice) {
      message.warning("Please select a voice first");
      return;
    }

    setPlayingVoiceLang(langKey);
    try {
      const response = await Api.post(
        "/api/tts/synthesize",
        {
          text: `${localizedName || fallbackName}`,
          languageCode: voiceCode,
          voiceName: voice,
          speakingRate: 1,
        },
        { responseType: "blob" },
      );

      if (isMounted.current && response.data) {
        const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.onended = () => URL.revokeObjectURL(audioUrl);
        await audio.play();
        message.success("Voice test played successfully");
      }
    } catch {
      if (isMounted.current) message.error("Failed to play test voice");
    } finally {
      if (isMounted.current) setPlayingVoiceLang(undefined);
    }
  };

  const handleTranslateCharacterMetadata = async () => {
    const formValues = form.getFieldsValue(true);
    const metadata = (formValues.metadata ?? {}) as ICharacterMetadata;
    const enMeta = metadata.en ?? {};

    const enName = String(enMeta.name ?? "").trim();
    const enDescription = String(enMeta.description ?? "").trim();
    const enStarterText = String(
      form.getFieldValue(["metadata", "en", "starterText"]) ??
        enMeta.starterText ??
        "",
    ).trim();
    const enTalkSubjects = talkSubjectsFromFormValue(enMeta.talkSubjects);

    if (!enName) {
      message.error("Fill in the English name first");
      return;
    }

    setTranslatingCharacterMetadata(true);
    try {
      const response = await Api.post(
        "/api/admin/panel/characters/translate-metadata",
        {
          name: enName,
          ...(enDescription ? { description: enDescription } : {}),
          ...(enStarterText ? { starterText: enStarterText } : {}),
          ...(enTalkSubjects.length ? { talkSubjects: enTalkSubjects } : {}),
        },
      );
      const translations = response.data?.data as
        | CharacterMetadataTranslation
        | undefined;
      if (!translations || !isMounted.current) {
        message.error("AI returned no translations");
        return;
      }

      const merged = mergeCharacterMetadataTranslations(metadata, translations);
      const voiceAssignments = await Promise.all(
        CHARACTER_LANGUAGES.map(async (lang) => {
          const cached = voicesByLang[lang.voiceCode];
          const voices = cached?.length
            ? cached
            : await fetchVoicesForLanguage(lang.voiceCode);
          return {
            key: lang.key,
            label: lang.label,
            voice: randomNaturalOrChirpVoice(voices),
          };
        }),
      );
      if (!isMounted.current) return;

      const missingVoiceLanguages: string[] = [];
      for (const assignment of voiceAssignments) {
        if (!assignment.voice) {
          missingVoiceLanguages.push(assignment.label);
          continue;
        }
        merged[assignment.key] = {
          ...(merged[assignment.key] ?? {}),
          voice: assignment.voice,
        };
      }

      form.setFieldsValue({ metadata: merged });
      message.success("Translated character metadata from English");
      if (missingVoiceLanguages.length) {
        message.warning(
          `No Chirp or Neural voice for ${missingVoiceLanguages.join(", ")}`,
        );
      }
    } catch {
      if (isMounted.current) message.error("Failed to translate metadata");
    } finally {
      if (isMounted.current) setTranslatingCharacterMetadata(false);
    }
  };

  const languageTabItems = CHARACTER_LANGUAGES.map((lang) => ({
    key: lang.key,
    label: lang.label,
    forceRender: true,
    children: (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              name={["metadata", lang.key, "name"]}
              label="Name"
              rules={[{ required: false }]}
            >
              <Input placeholder={`Name in ${lang.label}`} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item label="Voice">
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name={["metadata", lang.key, "voice"]}
                  noStyle
                  rules={[{ required: false }]}
                >
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select a voice"
                    loading={loadingVoicesByLang[lang.voiceCode]}
                    disabled={!voicesByLang[lang.voiceCode]?.length}
                    options={(voicesByLang[lang.voiceCode] ?? []).map(
                      (voice) => ({
                        label: `${voice.name} (${voice.ssmlGender})`,
                        value: voice.name,
                      }),
                    )}
                  />
                </Form.Item>
                <Button
                  icon={<PlayCircleOutlined />}
                  loading={playingVoiceLang === lang.key}
                  onClick={() => playTestVoice(lang.key, lang.voiceCode)}
                  title="Play test voice"
                />
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name={["metadata", lang.key, "description"]}
          label="Bio (Markdown)"
          rules={[
            {
              required: lang.key === "en",
              message: "Please enter a bio in English",
            },
          ]}
        >
          <Input.TextArea rows={3} placeholder={`Bio in ${lang.label}`} />
        </Form.Item>

        <Form.Item
          name={["metadata", lang.key, "starterText"]}
          label="starterText"
        >
          <Input.TextArea
            rows={3}
            placeholder={`Starter text in ${lang.label}`}
          />
        </Form.Item>

        <Form.Item
          name={["metadata", lang.key, "talkSubjects"]}
          label="Talk subjects (one per line)"
        >
          <Input.TextArea
            rows={4}
            placeholder="One per line. Optional: question | message (dashboard label | first chat message). Plain lines are translated as-is."
          />
        </Form.Item>
      </>
    ),
  }));

  const editForm = (
    <Form
      initialValues={{
        isActive: false,
        isAvailableInIran: true,
        isPremium: false,
        isForMission: false,
        ...currentChar,
        metadata: currentChar
          ? migrateCharacterMetadataFromLegacy(currentChar)
          : normalizeCharacterMetadata(undefined),
      }}
      form={form}
      layout="vertical"
      onFinish={createCharacter}
    >
      <Row gutter={16}>
        <Col
          lg={{ push: 4, span: 16 }}
          md={{ push: 4, span: 16 }}
          sm={{ span: 20, push: 2 }}
          xs={24}
        >
          <Form.Item name="_id" hidden />

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <Avatar
              alt="Character avatar"
              size={80}
              src={
                selectedAvatarPath
                  ? mediaPreviewSrc(selectedAvatarPath)
                  : currentChar?._id
                    ? mediaPreviewSrc(
                        `images/characters/${currentChar._id}_sm.jpg`,
                      )
                    : undefined
              }
            >
              {!selectedAvatarPath && !currentChar?._id && <PlusOutlined />}
            </Avatar>
            <Space wrap>
              <Button
                icon={<FolderOpenOutlined />}
                onClick={() => setAvatarModalOpen(true)}
              >
                Select from file manager
              </Button>
              {selectedAvatarPath && (
                <Button
                  danger
                  type="link"
                  onClick={() => setSelectedAvatarPath(undefined)}
                >
                  Clear
                </Button>
              )}
            </Space>
          </div>

          <Form.Item
            name="name"
            label="Default name"
            rules={[{ required: true }]}
            tooltip="Used internally and as fallback when a language-specific name is not set"
          >
            <Input />
          </Form.Item>

          <Form.Item name="jobTitle" label="Job Title" rules={[]}>
            <Input placeholder="e.g. Language Teacher, Conversation Partner, Tutor" />
          </Form.Item>

          <div style={METADATA_SECTION_STYLE}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div style={{ ...METADATA_SECTION_LABEL_STYLE, marginBottom: 0, flex: 1 }}>
                Character metadata (shown to learners per language)
              </div>
              {renderMetadataTranslateButton({
                loading: translatingCharacterMetadata,
                onClick: handleTranslateCharacterMetadata,
                tooltip:
                  "Translate English name, bio, starter text, and talk subjects to all languages, and assign a random Neural or Chirp voice to each language",
              })}
            </div>
            {isCompact && (
              <Select
                value={activeLanguageTab}
                onChange={(value) => setActiveLanguageTab(String(value))}
                style={{ width: "100%", marginBottom: 12 }}
                options={CHARACTER_LANGUAGES.map((lang) => ({
                  value: lang.key,
                  label: lang.label,
                }))}
              />
            )}
            <Tabs
              activeKey={activeLanguageTab}
              onChange={setActiveLanguageTab}
              tabBarStyle={isCompact ? { display: "none" } : undefined}
              items={languageTabItems}
            />
          </div>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                valuePropName="checked"
                name="isActive"
                label="Is Active"
              >
                <Checkbox disabled={!canManageActivation} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                valuePropName="checked"
                name="isPremium"
                label="Is Premium"
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                valuePropName="checked"
                name="isAvailableInIran"
                label="Is Available In Iran"
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                valuePropName="checked"
                name="isForMission"
                label="Is For Mission"
                tooltip="Hidden from the app's all-characters list; used only within missions"
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Behavior" required>
            <div style={CHARACTER_INSTRUCTION_PLACEHOLDERS_HINT_STYLE}>
              Placeholders: {CHARACTER_INSTRUCTION_PLACEHOLDERS_TEXT}
            </div>
            <Form.Item name="behavior" noStyle rules={[{ required: true }]}>
              <Input.TextArea
                rows={isCompact ? 10 : 24}
                placeholder="System instruction for this character…"
              />
            </Form.Item>
          </Form.Item>

        </Col>
      </Row>
    </Form>
  );

  const handleAvatarSelected = async (fullUrl: string) => {
    const relative = toStoredMediaPath(fullUrl);
    setSelectedAvatarPath(relative);
    setAvatarModalOpen(false);
    message.success("Avatar selected");

    const charId = currentChar?._id ?? form.getFieldValue("_id");
    if (!charId) return;

    try {
      await uploadCharacterAvatarFromPath(charId, relative);
      if (isMounted.current) {
        message.success("Avatar uploaded successfully");
      }
    } catch {
      if (isMounted.current) {
        message.error("Failed to upload the image");
      }
    }
  };

  return (
    <Card style={{ overflowX: "clip" }}>
      <StickyFormActions
        onBack={() => (returnTo ? navigate(returnTo) : navigate(-1))}
        actions={
          isCreateMode || activeCharacterTab === "edit" ? (
            <Button
              loading={saving}
              type="primary"
              size="large"
              icon={<UserAddOutlined />}
              onClick={() => form.submit()}
            >
              {isCreateMode ? "Create" : "Save"}
            </Button>
          ) : undefined
        }
      />
      {isCreateMode ? (
        editForm
      ) : (
        <Tabs
          activeKey={activeCharacterTab}
          onChange={setCharacterTab}
          style={{ marginTop: 12 }}
          items={[
            { key: "edit", label: "Edit", children: editForm },
            {
              key: "messages",
              label: "Chat history",
              children: characterIdForMessages ? (
                <CharacterMessagesPanel
                  characterID={characterIdForMessages}
                  embedded
                />
              ) : (
                <div style={{ padding: 16 }}>Loading character…</div>
              ),
            },
          ]}
        />
      )}

      <Modal
        title="File manager"
        open={avatarModalOpen}
        onCancel={() => setAvatarModalOpen(false)}
        footer={null}
        width={isCompact ? "100vw" : "min(1320px, 96vw)"}
        style={{ top: isCompact ? 0 : 24, maxWidth: "100vw", paddingBottom: 0 }}
        bodyStyle={{
          maxHeight: isCompact ? "calc(100vh - 110px)" : "calc(100vh - 180px)",
          overflow: "auto",
          padding: isCompact ? 12 : 24,
        }}
        destroyOnClose
      >
        <AdminFileManager
          defaultFolder="images/characters"
          onSelectUrl={handleAvatarSelected}
        />
      </Modal>
    </Card>
  );
}
